import { useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { useAppStore } from '@/lib/store/useAppStore'
import { useRestaurants } from '@/lib/queries/useRestaurants'
import { Skeleton } from '@/components/ui/Skeleton'
import { useRealtimeSync } from '@/hooks/useRealtimeSync'
import { useSyncEngine } from '@/hooks/useSyncEngine'
import { runInitialSync } from '@/lib/db/initialSync'
import { useSyncProgressStore } from '@/lib/store/useSyncProgressStore'
import { useNetworkStore } from '@/lib/store/useNetworkStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { AssistantWidget } from '@/components/assistant/AssistantWidget'
import { MobileNavigation } from './MobileNavigation'

export function DashboardLayout() {
  const { sidebarCollapsed, restaurantId, setRestaurantId } = useAppStore()
  const isOnline = useNetworkStore((s) => s.isOnline)
  const isMobile = useIsMobile()
  const { start, setProgress, done } = useSyncProgressStore()
  useRealtimeSync()
  useSyncEngine()

  const navigate = useNavigate()
  const { data: resp, isLoading } = useRestaurants()

  useEffect(() => {
    if (isLoading) return
    const restaurants = resp?.data
    if (!restaurants || restaurants.length === 0) {
      navigate('/onboarding', { replace: true })
    } else if (!restaurantId) {
      setRestaurantId(restaurants[0].documentId)
    }
  }, [resp, isLoading, restaurantId, setRestaurantId, navigate])

  // Run initial sync whenever restaurantId becomes available and we're online
  useEffect(() => {
    if (!restaurantId || !isOnline) return
    start()
    runInitialSync(restaurantId, setProgress)
      .catch(console.error)
      .finally(done)
  }, [restaurantId, isOnline]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg-base)]">
        <div className="space-y-4 w-64">
          <Skeleton variant="rectangular" height={20} />
          <Skeleton variant="rectangular" height={16} width="60%" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-dvh overflow-x-clip bg-[var(--color-bg-base)]">
      <Sidebar />
      <div
        className="min-w-0 max-w-full transition-all duration-200 ease-[var(--ease-smooth)]"
        style={{ marginLeft: isMobile ? 0 : sidebarCollapsed ? 64 : 240 }}
      >
        <Topbar />
        <main className="min-w-0 max-w-full overflow-x-clip p-3 pb-[calc(5rem+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
          <Outlet />
        </main>
      </div>
      <MobileNavigation />
      <AssistantWidget />
    </div>
  )
}
